import { useEffect, useState } from 'react'
import { Gauge, Loader2, Search } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, getApiErrorMessage } from '@/lib/api'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface RateLimitViolation {
  id: number
  account_id: string | null
  api_key_id: string | null
  limit_type: string
  limit_value: number
  attempted: number
  endpoint: string | null
  ip_address: string | null
  created_at: string
}

const LIMIT_TYPES = ['api_daily', 'email_daily', 'email_hourly', 'email_per_minute', 'burst', 'domain_warmup']

// CHECKPOINT: PRD-09 FR-9.3 — Rate limit violations list
export function RateLimitViolationsTab() {
  const [violations, setViolations] = useState<RateLimitViolation[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [accountId, setAccountId] = useState('')
  const [limitType, setLimitType] = useState('')
  const LIMIT = 50

  const fetchViolations = async (newOffset = offset) => {
    setLoading(true)
    try {
      const params: Record<string, any> = { page: Math.floor(newOffset / LIMIT) + 1, per_page: LIMIT }
      if (accountId) params.account_id = accountId
      if (limitType) params.limit_type = limitType
      const res = await api.get('/admin/security/rate-limit-violations', { params })
      setViolations(res.data.violations || [])
      setTotal(res.data.total || 0)
      setOffset(newOffset)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load rate limit violations'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchViolations(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Input placeholder="Account ID" value={accountId} onChange={(e) => setAccountId(e.target.value)} className="max-w-xs" />
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={limitType}
          onChange={(e) => setLimitType(e.target.value)}
        >
          <option value="">All limit types</option>
          {LIMIT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <Button onClick={() => fetchViolations(0)} size="sm" variant="secondary">
          <Search className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Rate Limit Violations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Limit Type</th>
                  <th className="px-4 py-3 text-left font-medium">Account</th>
                  <th className="px-4 py-3 text-left font-medium">Limit / Attempted</th>
                  <th className="px-4 py-3 text-left font-medium">Endpoint</th>
                  <th className="px-4 py-3 text-left font-medium">IP</th>
                  <th className="px-4 py-3 text-left font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : violations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No rate limit violations
                    </td>
                  </tr>
                ) : (
                  violations.map((v) => (
                    <tr key={v.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{v.limit_type}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {v.account_id ? v.account_id.slice(0, 8) + '...' : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {v.attempted} / {v.limit_value}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{v.endpoint || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{v.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(v.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={fetchViolations} />
        </CardContent>
      </Card>
    </div>
  )
}
